import React, { useEffect, useRef, useState } from "react";
import { workspaces as workspacesApi } from "apis/callables";
import type StripeTypes from "stripe";
import { logger } from "js/react/views/UserOptions/panes/BillingPane/Components/PaymentMethodBlock";
import { getPromotionCodeValidationError, normalizePromotionCode } from "../../utils/promotionCode";

// Common interface for promo form props
export interface IPromoFormProps {
    verifyPromoCode: (promoCode: StripeTypes.PromotionCode) => Promise<void>;
    initialPromotionCode?: string;
    prefillOnly?: boolean
}

// Common hook for promo form logic
export const usePromoFormLogic = (
    verifyPromoCode: (promoCode: StripeTypes.PromotionCode) => Promise<void>,
    initialPromotionCode?: string,
    prefillOnly?: boolean
) => {
    const [isAddingPromo, setIsAddingPromo] = useState(Boolean(initialPromotionCode));
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [promoCode, setPromoCode] = useState(normalizePromotionCode(initialPromotionCode) ?? "");
    const hasAttemptedAutoApply = useRef(false);

    const handleAddPromo = () => {
        setIsAddingPromo(true);
    };

    const handleChangePromoCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newCode = e.target.value;

        setPromoCode(newCode);
        setPromoError(null);

        if (promoCode !== "" && newCode === "") {
            setIsAddingPromo(false);
        }
    };

    const applyPromoCode = async (code: string) => {
        const normalizedPromotionCode = normalizePromotionCode(code);
        const validationError = getPromotionCodeValidationError(normalizedPromotionCode);

        if (validationError) {
            setPromoError(validationError);
            return;
        }

        setIsApplyingPromo(true);

        try {
            const promotionCode = await workspacesApi.getPromotionCode({ promotionCode: normalizedPromotionCode });
            await verifyPromoCode(promotionCode);
        } catch (err) {
            logger.error(err, `[PromoForm] handleApplyPromo() failed`);
            setPromoError(err.message);
        } finally {
            setIsApplyingPromo(false);
        }
    };

    const handleApplyPromo = async () => {
        await applyPromoCode(promoCode);
    };

    useEffect(() => {
        const normalizedPromotionCode = normalizePromotionCode(initialPromotionCode);

        if (!normalizedPromotionCode || hasAttemptedAutoApply.current || prefillOnly) {
            return;
        }

        hasAttemptedAutoApply.current = true;
        setIsAddingPromo(true);
        setPromoCode(normalizedPromotionCode);
        void applyPromoCode(normalizedPromotionCode);
    }, [initialPromotionCode, prefillOnly, verifyPromoCode]);

    return {
        isAddingPromo,
        isApplyingPromo,
        promoError,
        promoCode,
        handleAddPromo,
        handleChangePromoCode,
        handleApplyPromo
    };
};
